import React, { useState } from 'react';
import { apiService } from '@/services/api';

// §otp (docs/BACKEND_PHONE_OTP_AND_LEAD_CAPTURE_PRD.md): SMS phone verification.
// Dark by default — only active when REACT_APP_OTP_ENABLED='1'. Verification now
// lives on the "How can we reach you?" contact slide (BasicContactStep), under
// the phone field, so it applies to EVERY visit type and tenant (that step is
// shared by all 8 intake forms). The resulting phoneVerifyToken rides the
// basic-contact answer to checkout, where it's relayed to the Square payment so
// the BE can trust the SMS-verified phone server-side (HMAC-bound to the phone).
export const OTP_ENABLED = process.env.REACT_APP_OTP_ENABLED === '1';

// Inline phone-verification control rendered directly beneath the Phone Number
// field. Renders nothing when the flag is off or the phone isn't complete, so
// the contact step is unchanged until the feature lights up.
export function PhoneVerify({ phone, verified, onVerified }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!OTP_ENABLED || !phone) return null;

  if (verified) {
    return (
      <div className="flex items-center gap-1.5 mt-2 text-sm text-[#2d8a5f]" data-testid="phone-verified">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Phone number verified
      </div>
    );
  }

  const send = async () => {
    setBusy(true); setError('');
    try { await apiService.sendOtp(phone); setSent(true); }
    catch (e) { setError('We couldn’t send the code. Check your number and try again.'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 4) return;
    setBusy(true); setError('');
    try {
      const r = await apiService.verifyOtp(phone, code.trim());
      if (r && r.verified) { onVerified && onVerified(r.phoneVerifyToken || null); }
      else if (r && r.locked) setError('Too many attempts. Request a new code in a few minutes.');
      else setError('That code didn’t match. Please try again.');
    } catch (e) { setError('Verification failed. Request a new code.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="phone-otp">
      {!sent ? (
        <button type="button" data-testid="otp-send" onClick={send} disabled={busy}
          className="text-sm font-medium text-[#2d8a5f] underline underline-offset-2 disabled:opacity-50">
          {busy ? 'Sending code…' : 'Verify phone number'}
        </button>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-1.5">Enter the 6-digit code we texted you.</p>
          <div className="flex items-center gap-2">
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code" data-testid="otp-code-input"
              className="form-input flex-1 text-center tracking-widest"
            />
            <button type="button" data-testid="otp-verify" onClick={verify} disabled={busy || code.trim().length < 4}
              className="rounded-lg bg-[#2d8a5f] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 whitespace-nowrap">
              {busy ? 'Verifying…' : 'Verify'}
            </button>
          </div>
          <button type="button" data-testid="otp-resend" onClick={send} disabled={busy}
            className="mt-1.5 text-xs text-[#2d8a5f] underline underline-offset-2 disabled:opacity-50">
            Resend code
          </button>
        </>
      )}
      {error && <p className="text-amber-700 text-xs mt-1.5" data-testid="otp-error">{error}</p>}
    </div>
  );
}
